import React, { useState } from "react";
import { ASC, DESC, NO_DATA_AVAILABLE } from "../../Constants/Strings";

// tableColumns: Actual Column Names (required) Array of String
// tableRows: Table Rows object (required) Array of objects
// columnLabels: Column Labels to be shown on UI (key value JSON)
// renderField: Formatting on row data function
// sortingColumn: applies sorting on this Column

//TODO: Validation for sorting.

const GenericTable = ({
  tableColumns,
  tableRows,
  columnLabels = null,
  rowClick = null,
  headerClassName = null,
  renderField = null,
  className = null,
  fieldClick = null,
  sortingColumn = null,
  customError = "",
  enableGridLayout = false,
  mobileGridColumns = null,
  renderMobileCard = null,
  enableMobileCardView = true,
  ...rest
}) => {
  const defaultClasses =
    "w-full text-left rtl:text-right text-gray-500 dark:text-gray-400";
  const classes = `${defaultClasses} ${className}`;
  const defaultMobileGridCols = 2;
  const [sortOrder, setSortOrder] = useState(DESC);

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === ASC ? DESC : ASC);
  };

  const sortRequests = (
    tableRows = [],
    sortBy = sortingColumn,
    sortOrder = ASC
  ) => {
    if (tableRows && tableRows.length > 0) {
      return tableRows.sort((a, b) => {
        const dateA = new Date(a[sortBy]);
        const dateB = new Date(b[sortBy]);
        return sortOrder === ASC ? dateA - dateB : dateB - dateA;
      });
    }
    return [];
  };

  const sortedRows = sortRequests(tableRows, sortingColumn, sortOrder);
  const error = customError || NO_DATA_AVAILABLE;

  return (
    <>
      {/* ===================== DESKTOP / WEB VIEW ===================== */}
      <div className="hidden md:block">
        <table className={`${classes} min-w-full`} {...rest}>
          <thead
            className={`text-sm text-gray-700 bg-gray-50 dark:bg-gray-700 dark:text-gray-400 ${headerClassName}`}
          >
            <tr>
              {tableColumns.map((key) => (
                <th
                  key={key}
                  scope="col"
                  className="sticky top-0 z-10 bg-gray-50 px-4 py-3"
                >
                  {columnLabels ? columnLabels[key] : key}
                  {key === sortingColumn && (
                    <button onClick={toggleSortOrder} className="ml-2 text-sm">
                      {sortOrder === ASC ? "↑" : "↓"}
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows?.map((row, index) => (
              <tr
                key={index}
                className={`border-b ${rowClick ? "cursor-pointer" : ""} ${index % 2 === 0
                    ? "bg-white dark:bg-gray-900"
                    : "bg-gray-50 dark:bg-gray-800"
                  }`}
                onClick={rowClick ? () => rowClick(row) : null}
              >
                {tableColumns.map((key, colIndex) => (
                  <td
                    key={key}
                    className={`px-4 py-4 text-sm text-wrap break-words ${fieldClick ? "cursor-pointer" : ""
                      } ${colIndex === 0
                        ? "font-medium text-gray-900 whitespace-nowrap dark:text-white"
                        : ""
                      }`}
                    onClick={fieldClick ? () => fieldClick(key, row) : null}
                  >
                    <div className="truncate max-w-[200px] overflow-hidden">
                      {renderField ? renderField(key, row) : row[key]}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            {!sortedRows || sortedRows?.length === 0 ? (
              <tr>
                <td
                  className="text-lg text-center p-4"
                  colSpan={tableColumns.length}
                >
                  {error}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {/* ===================== MOBILE VIEW ===================== */}
      {enableMobileCardView ? (
        // ✅ Card View on Mobile
        <div className="block md:hidden">
          {sortedRows?.map((row, index) =>
            renderMobileCard ? (
              <div key={index}>{renderMobileCard(row, renderField)}</div>
            ) : (
              <div
                key={index}
                className={`border rounded-md text-sm shadow-md p-4 ${enableGridLayout
                    ? `grid grid-cols-${mobileGridColumns === null
                      ? defaultMobileGridCols
                      : mobileGridColumns
                    } gap-2`
                    : ""
                  } ${rowClick ? "cursor-pointer" : ""}`}
                onClick={rowClick ? () => rowClick(row) : null}
              >
                {tableColumns.map((key, colIndex) => (
                  <div key={key} className="flex flex-col text-left min-w-0">
                    <span
                      className={`text-gray-900 dark:text-gray-100 truncate ${fieldClick ? "cursor-pointer" : ""
                        } ${colIndex === 0 ? "font-bold" : ""}`}
                      onClick={fieldClick ? () => fieldClick(key, row) : null}
                      title={
                        renderField
                          ? String(renderField(key, row))
                          : String(row[key] ?? "")
                      }
                    >
                      {renderField ? renderField(key, row) : row[key]}
                    </span>
                  </div>
                ))}
              </div>
            )
          )}

          {!sortedRows || sortedRows?.length === 0 ? (
            <div className="text-lg text-center h-full p-2">{error}</div>
          ) : null}
        </div>
      ) : (
        // ✅ Table View on Mobile (when card view disabled)
        <div className="block md:hidden overflow-x-auto">
          <table className={`${classes} min-w-full`} {...rest}>
            <thead
              className={`text-sm text-gray-700 bg-gray-50 dark:bg-gray-700 dark:text-gray-400 ${headerClassName}`}
            >
              <tr>
                {tableColumns.map((key) => (
                  <th key={key} scope="col" className="px-4 py-3">
                    {columnLabels ? columnLabels[key] : key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows?.map((row, index) => (
                <tr
                  key={index}
                  className={`border-b ${index % 2 === 0 ? "bg-white" : "bg-gray-50"
                    }`}
                >
                  {tableColumns.map((key) => (
                    <td key={key} className="px-4 py-2 text-sm">
                      {renderField ? renderField(key, row) : row[key]}
                    </td>
                  ))}
                </tr>
              ))}
              {!sortedRows || sortedRows?.length === 0 ? (
                <tr>
                  <td
                    className="text-lg text-center p-4"
                    colSpan={tableColumns.length}
                  >
                    {error}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default GenericTable;
